import { DesktopContainer } from '@/components/DesktopContainer/DesktopContainer';
import { H1, H2 } from '@/components/ui/typography';

interface TermsAndConditionsProps {
    showTitle: boolean;
    showLastModified: boolean;
}

const AcceptanceOfTerms = () => (
    <>
        <H2 variant="type-headline-small">Acceptance of these Terms of Use</H2>
        <div>
            These terms and conditions (collectively, the “Terms of Use”) are agreed between you
            and Massachusetts Higher Education Assistance Corporation, d/b/a Britebound™
            (“Britebound”, “we”, or “us”). These Terms of Use govern your access to and use of our
            websites and services available at https://gozig.com, including any related subdomains,
            and (the “Services”), whether you are a guest or registered or unregistered user (“User”).
            The Services are designed to support young people, generally ages 13 to 24, in exploring
            careers, identifying interests, and engaging in self-guided learning through personalized
            assessments and exploration of career-related information. As used in these Terms of
            Use, the “Services” includes all of their pages, functionality and features, and all services,
            content, information, links and other materials (“Materials”) made available on or through
            the Services. As used in these Terms of Use, the “Services” do not include any services,
            videos, content, information, links, or other materials offered on or through third-party
            websites, such as YouTube, TikTok, Webz.io, and/or websites of our partners (“Third-Party Services”)
        </div>
        <div>
            All personal information that we may collect on the Services, including information you provide to register
            with the Services and information collected by us through your use of any features on the Services, is
            subject to our
            {' '}
            <a href="/privacy-policy" className="text-blue-600 dark:text-blue-400 underline">Privacy Policy</a>
            . By using the Services, you
            consent to all actions taken by us with respect
            to your personal information in compliance with the Privacy Policy.
        </div>
        <div>
            Please read these Terms of Use carefully before you start to use the Services. By using the Services or by
            clicking to accept or agree to the Terms of Use when this option is made available to you, you accept and
            agree to be bound by and comply with these Terms of Use and acknowledge that you have read and understand
            our Privacy Policy. If you do not agree to these Terms of Use, you must not access or use the Services.
        </div>
        <div>
            The Services are directed to users who are 13 years of age or older and who reside in the United States
            (including its territories and possessions, “United States”). Certain functionality of the Services,
            including access to all or any part of the Services, may be limited or disabled for users under 13 years of
            age, and users under 13 years of age are not permitted to register an account on the Services. If you are
            under 18 years of age, you represent and warrant that a parent or legal guardian has reviewed and agreed to
            be bound by and comply with these Terms of Use on his or her behalf and on your behalf. If you are the
            parent or legal guardian of a user under 18 years of age, you agree to these Terms of Use on your own and
            your child’s behalf, specifically including the Disclaimer of Warranties and Limitation of Liability, below.
            By using the Services, you represent and warrant that you meet all of the foregoing eligibility
            requirements. If you do not meet all of these requirements, you must not access or use the Services.
        </div>
        <div>
            Our Services utilize certain third-party services and link to Third-Party Services. Except as otherwise
            disclosed in our Privacy Policy, your access to Third-Party Services, whether or not initiated through the
            Services, is subject to the terms of service, privacy policies, and other terms and conditions of such
            Third-Party Services, as applicable, and you should be sure that you (and, if you are under the age of 18,
            your parent or legal guardian) have read and understand such terms of service, privacy policies, and other
            terms and conditions.
        </div>
    </>
);

const AiEnabledFeatures = () => (
    <>
        <H2 variant="type-headline-small">AI-Enabled Features; AI-Generated Content</H2>
        <div>
            Certain features or Materials made available through the Services may be generated, supported, or enhanced
            using automated technologies, including artificial intelligence (“AI”), and some outputs may be provided by
            third parties. AI-generated or AI-assisted content and outputs (including, without limitation, summaries,
            suggestions, and data) may be inaccurate, incomplete, misleading, or not up to date. AI-generated outputs are
            provided automatically and are not reviewed or verified by us unless we expressly state otherwise. You are
            responsible for independently verifying any information you obtain through the Services before relying on it.
            AI-enabled features and any AI-generated or AI-assisted content are provided for general informational purposes
            only and do not constitute professional (including legal, medical, financial, or career) advice. Your use of,
            and reliance on, AI-generated or AI-assisted content is at your own risk. We may modify, limit, suspend, or
            discontinue any AI-enabled features at any time.
        </div>
        <div>
            <span className="font-semibold">User Inputs.</span>
            {' '}
            If you submit information, questions, prompts, or other content to any AI-enabled feature, you represent and
            warrant that you have the right to submit such content and that it does not violate these Terms of Use. Do
            not submit confidential information (including information about other individuals) or any sensitive
            personal information through AI-enabled features unless explicitly requested by the Services.
        </div>
    </>
);

const TermsEffectiveFromAccess = () => (
    <div>
        As between you and us, these Terms of Use are effective from the first moment you access the Services and
        will remain effective thereafter in perpetuity, unless terminated earlier as provided hereinafter.
    </div>
);

const ChangesToTerms = () => (
    <>
        <H2 variant="type-headline-small">Changes to these Terms of Use</H2>
        <div>
            We may revise and/or update these Terms of Use from time to time in our sole discretion. All changes are
            effective immediately when we post them and apply to all of your access to and use of the Services
            thereafter. If you continue to use the Services after we post the revised and/or updated Terms of Use, it
            means that you accept and agree to the revisions and/or updates. When our revisions and/or updates to these
            Terms of Use would materially affect your rights, it is our practice to post a notice that we have made such
            revisions and/or updates to these Terms of Use on the applicable Services. You are expected to check this
            page from time to time so you are aware of any changes, as they are binding on you. The date these Terms of
            Use were last revised and/or updated is identified at the top of this page.
        </div>
    </>
);

const NoUseOutsideOfUS = () => (
    <>
        <H2 variant="type-headline-small">No Use or Access by Users not Located in the United States</H2>
        <div>
            The Services are made available from the United States and the Services are strictly for use by and solely
            directed to persons located in the United States. If you are not in the United States, YOU MAY NOT ACCESS OR
            USE THE SERVICES. We make no claims that the Services or any of their Materials are accessible or
            appropriate outside of the United States. The Services may not comply with laws of certain countries; if you
            are not in the United States, your access to and use of the Services may not be legal. If you access or use
            the Services from outside the United States, you do so at your own risk and are responsible for compliance
            with local laws.
        </div>
    </>
);

const AccessingTheService = () => (
    <>
        <H2 variant="type-headline-small">Accessing the Services and Account Security</H2>
        <div>
            We reserve the right to withdraw or amend the Services, including the right to remove any links to
            Third-Party Services, in our sole discretion and without notice to you. We will not be liable if all or any
            part of the Services becomes unavailable for any reason, at any time, or for any period. From time to time,
            we may restrict access to some parts of the Services, or entire Services, to all users or particular users,
            without notice, in our sole discretion.
        </div>
        <div>
            To access the Services or any of their Materials or any Third-Party Services made available on or through
            the Services, you may be asked to provide certain registration details or other information. It is a
            condition of your use of the Services that all the information you provide on the Services is correct,
            current, and complete.
        </div>
        <div>
            You are responsible for both:
            <ul className="list-disc pl-6">
                <li>
                    Making all arrangements (including obtaining any internet connection) necessary for you to have
                    access to the Services; and
                </li>
                <li>
                    Ensuring that all persons who access or use the Services through your internet connection agree to
                    these Terms of Use and comply with them.
                </li>
            </ul>
        </div>
        <div>
            You acknowledge that your account is personal to you. If you choose or are provided with a user name, a
            password, or any other information as part of our security procedures, you must keep that information
            confidential and not disclose it to any other person or entity. You may not provide any other person with
            access to the Services or portions of them using your user name, password, or other security information.
            You are responsible for all activity occurring under your account or with use of your user name and
            password.
        </div>
        <div>
            You agree to notify us immediately of any unauthorized access to or use of your user name or password or any
            other breach of security. You may report unauthorized access by sending an email to
            {' '}
            <a className="text-blue-600 dark:text-blue-400 underline" href="mailto:support@gozig.com">support@gozig.com</a>
            .
        </div>
        <div>
            We have the right to disable any user name, password, or other identifier, whether chosen by you or provided
            by us, at any time in our sole discretion.
        </div>
    </>
);

const Trademarks = () => (
    <>
        <H2 variant="type-headline-small">Trademarks</H2>
        <div>
            The Britebound name, the Britebound trademark, the Britebound logo, and all related
            names, trademarks, logos, product and service names, designs, and slogans, including
            those of our platforms (including, without limitation, GoZig, Futurescape, Next Voice are
            trademarks of Britebound and its affiliates or licensors. You must not use such
            trademarks without the prior written permission of Britebound. All other names,
            trademarks, logos, product and service names, designs, and slogans on the Services are
            the trademarks of their respective owners.
        </div>
    </>
);

const IntellectualPropertyRights = () => (
    <>
        <H2 variant="type-headline-small">Intellectual Property Rights</H2>
        <div>
            The Services and their Materials (including all information, software, text, displays,
            images, video, and audio, and the design, selection, and arrangement thereof) are owned
            by Britebound, its licensors, or other providers of such Materials, and are protected by
            United States and international copyright, trademark, patent, trade secret, and other
            intellectual property or proprietary rights laws. All rights not expressly granted to you in
            these Terms of Use are reserved by Britebound. You do not acquire any interest, right, or
            title in or to the Services or any of their Materials.
        </div>
        <div>
            These Terms of Use permit you to access and use the Services for your personal, non-commercial use only, and
            you must not access or use the Services for any commercial purpose. You must not reproduce, distribute,
            modify, create derivative works of, publicly display, publicly perform, republish, download, store, or
            transmit any part of the Services, including any of its Materials, except as follows:
            <ul className="list-disc pl-6">
                <li>
                    Your computer may temporarily store copies of the Services’ Materials in its RAM, cache, or other
                    transient, short-term memory incidental to your accessing and viewing those Materials;
                </li>
                <li>
                    You may store files that are automatically cached by your Web browser for purposes of using the
                    Services in accordance with these Terms of Use;
                </li>
                <li>
                    You may print or download one copy of a reasonable number of pages of the Services for your own
                    personal, non-commercial use and not for further reproduction, publication, or distribution; and
                </li>
                <li>
                    If we provide social media features with certain Materials, you may take such actions as are enabled
                    by such features.
                </li>
            </ul>
        </div>
        <div>
            You must not:
            <ul className="list-disc pl-6">
                <li>Modify or make any changes to copies of any of the Services’ Materials;</li>
                <li>
                    Use any illustrations, photographs, video or audio sequences, or graphics separately from the
                    accompanying text; or
                </li>
                <li>
                    Delete or alter any copyright, trademark, or other proprietary rights notices from copies of the
                    Materials.
                </li>
            </ul>
        </div>
        <div>
            If you wish to make any use of any of the Services’ Materials other than as described in these Terms of Use,
            please contact us at:
            {' '}
            <a className="text-blue-600 dark:text-blue-400 underline" href="mailto:support@gozig.com">support@gozig.com</a>
            .
        </div>
        <div>
            Without limiting any other of our rights to suspend or terminate your access to the Services, if you print,
            copy, modify, download, or otherwise use or provide any other person with access to any part of the Services
            in breach of these Terms of Use, we may suspend your right to use the Services without notice, and you must,
            at our option, immediately return or destroy any copies of the Materials you have made. In addition, we
            reserve our right to seek any other applicable remedies, including our right to bring any claims related to
            infringement of intellectual property.
        </div>
    </>
);

const ProhibitedUses = () => (
    <>
        <H2 variant="type-headline-small">Prohibited Uses</H2>
        <div>
            You may use the Services only for lawful purposes and in accordance with these Terms of Use. Any use of the
            Services or their Materials in a way not expressly permitted by these Terms of Use is a breach of these
            Terms of Use. In addition to any other prohibitions stated in these Terms of Use, you agree not to use the
            Services or any of their Materials:
            <ul className="list-disc pl-6">
                <li>
                    In any way that violates applicable law (including, without limitation, any laws regarding the
                    export of data or software to and from the United States or other countries);
                </li>
                <li>
                    Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code of any part
                    of the Services.
                </li>
                <li>
                    For the purpose of exploiting, harming, or attempting to exploit or harm minors in any way by
                    exposing them to inappropriate content, asking for Personal Information, or otherwise;
                </li>
                <li>
                    To send, knowingly receive, upload, download, use, or re-use any material that does not comply with
                    the Content Standards set out in these Terms of Use;
                </li>
                <li>
                    To transmit, or procure the sending of, any advertising or promotional material, including any “junk
                    mail,” “chain letter,” “spam,” or any other similar solicitation or material;
                </li>
                <li>
                    To impersonate or attempt to impersonate Britebound (including any Services of
                    Britebound), an Britebound employee, another user, or any other person or entity
                    (including, without limitation, by using e-mail addresses, user names, trademarks,
                    or other identifiers associated with any of the foregoing); or
                </li>
                <li>
                    To engage in any other conduct that restricts or inhibits anyone’s use or
                    enjoyment of the Services, or which, as determined by us in our sole discretion,
                    may harm Britebound or users of the Services, or expose us or them to liability.
                </li>
            </ul>
        </div>
        <div>
            Additionally, you agree not to:
            <ul className="list-disc pl-6">
                <li>
                    Use the Services in any manner that could disable, overburden, damage, or impair the Services or
                    interfere with or otherwise inhibit any other person’s or entity’s access to or use of the Services,
                    including their ability to engage in real-time activities through the Services;
                </li>
                <li>
                    Manipulate or otherwise engage with any Experience (as defined below) in a manner intended to
                    accumulate Points (as defined below) without completing or otherwise properly engaging with such
                    Experience, including, without limitation, by fast-forwarding through a video made available on or
                    through an Experience, skipping required content, using automated tools, or creating multiple
                    accounts to engage with Experiences;
                </li>
                <li>
                    Use any robot, spider, or other automatic device, process, or means to access the Services for any
                    purpose, including monitoring, scraping, or copying any of the Services’ Materials;
                </li>
                <li>
                    Use any AI-enabled feature to generate, submit, or disseminate unlawful, harmful, or deceptive
                    content, or to attempt to reverse engineer, extract, or infer the underlying models, prompts, or
                    system instructions used by the Services or any third-party AI provider.
                </li>
                <li>
                    Use any manual process to monitor or copy any of the Services’ Materials, or for any other purpose
                    not expressly authorized in these Terms of Use, without our prior written consent;
                </li>
                <li>
                    Use any device, software, or routine that interferes with or otherwise inhibits the proper working
                    of the Services;
                </li>
                <li>
                    Introduce any viruses, trojan horses, worms, logic bombs, or other software code or other material
                    which is malicious or technologically harmful;
                </li>
                <li>
                    Attempt to gain unauthorized access to, interfere with, damage, or disrupt any parts of the
                    Services, the servers on which the Services are stored, or any server, computer, database, or other
                    website connected to the Services;
                </li>
                <li>
                    Attack the Services via a denial-of-service attack or a distributed denial-of-service attack; or
                </li>
                <li>
                    Otherwise attempt to interfere with or inhibit the proper working of the Services.
                </li>
            </ul>
        </div>
    </>
);

const UserContributions = () => (
    <>
        <H2 variant="type-headline-small">User Contributions</H2>
        <div>
            The Services may contain interactive features that allow users to post, submit, publish, display, or
            transmit (hereinafter, “post”) content (collectively, “User Contributions”) on or through the Services
            (collectively, “Interactive Services”).
        </div>
        <div>
            All User Contributions must comply with the Content Standards set out in these Terms of Use. By providing
            any User Contribution on the Services, you grant us and our affiliates, partners, and service providers, and
            each of their and our respective licensees, successors, and assigns the right to use, reproduce, modify,
            perform, display, distribute, and otherwise disclose to third parties any such material for purposes of
            making the Services and their Materials and any Third-Party Services available to you and other users of the
            Services and improving the Services and our other products and services.
        </div>
        <div>
            You represent and warrant that:
            <ul className="list-disc pl-6">
                <li>
                    You own or control all rights in and to your User Contributions and have the right to grant the
                    license granted above to us and our affiliates, partners, and service providers, and each of their
                    and our respective licensees, successors, and assigns; and
                </li>
                <li>
                    All of your User Contributions do and will comply with these Terms of Use (including, without
                    limitation, the Content Standards).
                </li>
            </ul>
        </div>
        <div>
            You understand and acknowledge that:
            <ul className="list-disc pl-6">
                <li>
                    You are responsible for any User Contributions you post, submit, publish, display,
                    or transmit, and you, not Britebound, have full responsibility for such User
                    Contributions, including the legality, reliability, accuracy, and appropriateness of
                    such User Contributions;
                </li>
                <li>
                    We will not be responsible or liable to you or any third party for the content or accuracy of any
                    User Contributions posted by you or any other user of the Services or for any loss or unavailability
                    of your User Contributions; and
                </li>
                <li>
                    Your User Contributions may not be treated as confidential.
                </li>
            </ul>
        </div>
    </>
);

const ContentStandards = () => (
    <>
        <H2 variant="type-headline-small">Content Standards</H2>
        <div>
            The following content standards (the “Content Standards”) apply to any and all User Contributions and use of
            Interactive Services. User Contributions must in their entirety comply with all applicable law. Without
            limiting the foregoing, User Contributions must not:
            <ul className="list-disc pl-6">
                <li>
                    Contain any material that is defamatory, obscene, indecent, abusive, offensive, harassing, violent,
                    hateful, inflammatory, or otherwise objectionable;
                </li>
                <li>
                    Contain, exhibit, or promote sexually explicit or pornographic material, violence, or discrimination
                    based on race, sex, religion, nationality, disability, sexual orientation, or age;
                </li>
                <li>
                    Infringe or misappropriate any patent, trademark, trade secret, copyright, or other intellectual
                    property or other proprietary rights of any other person or entity;
                    <ul className="list-disc pl-6">
                        <li>
                            Violate the legal rights (including the rights of publicity and privacy) of others or
                            contain any material that could give rise to any civil or criminal liability under
                            applicable law or that otherwise may be in conflict with these Terms of Use or our Privacy
                            Policy;
                        </li>
                        <li>
                            Be likely to deceive any person;
                        </li>
                        <li>
                            Promote any illegal activity, or advocate, promote, or assist any unlawful act;
                        </li>
                        <li>
                            Cause annoyance, inconvenience, or needless anxiety or be likely to upset, embarrass, alarm,
                            or annoy any other person;
                        </li>
                        <li>
                            Impersonate any person, or misrepresent your identity or affiliation with any person or
                            organization;
                        </li>
                        <li>
                            Involve commercial activities or sales, such as contests, sweepstakes, and other sales
                            promotions, barter, or advertising; or
                        </li>
                        <li>
                            Give the impression that they emanate from or are endorsed by us or any other person or
                            entity, if that is not the case.
                        </li>
                    </ul>
                </li>
            </ul>
        </div>

    </>
);

const MonitoringAndEnforcement = () => (
    <>
        <H2 variant="type-headline-small">Monitoring and Enforcement; Termination</H2>
        <div>
            We have the right to:
            <ul className="list-disc pl-6">
                <li>
                    Remove or refuse to post any User Contributions for any or no reason in our sole discretion;
                </li>
                <li>
                    Take any action with respect to any User Contribution that we deem necessary or appropriate in our
                    sole discretion, including if we believe that such User Contribution violates the Terms of Use,
                    including the Content Standards, infringes any intellectual property right or other right of any
                    person or entity, threatens the personal safety of users of the Services or the public, or could
                    create liability for Britebound;
                </li>
                <li>
                    Disclose your identity or other information about you to any third party who claims that
                    information, content or other material posted, submitted, published, displayed, or transmitted on or
                    through the Services by you violates such third party’s rights, including such third party’s
                    intellectual property rights or their right of publicity or privacy;
                </li>
                <li>
                    Take appropriate legal action, including referral to law enforcement, for any illegal or
                    unauthorized use of the Services;
                </li>
                <li>
                    Terminate or suspend your access to all or part of the Services for any or no reason, including any
                    violation of these Terms of Use; or
                </li>
                <li>
                    Fully cooperate with any law enforcement authorities or court order requesting or directing us to
                    disclose the identity or other information of anyone posting, submitting, publishing, displaying, or
                    transmitting any information, content or other material on or through the Services.
                </li>
            </ul>
        </div>
    </>
);

const LinksToAndFromServices = () => (
    <>
        <H2 variant="type-headline-small">Links to and from the Services; Social Media Features</H2>
        <div>
            You may link to the Services, provided you do so in a way that is fair and legal and does not, as determined
            by us in our sole discretion, damage our reputation or take advantage of it, but you must not establish a
            link in such a way as to suggest any form of association, approval, or endorsement on our part without our
            express written consent.
        </div>
        <div>
            If the Services contain links to other websites or to Third-Party Services or other Materials available from
            or provided by third parties (including, without limitation, our partners), these links are provided for
            your convenience only. This includes links contained in advertisements, including banner advertisements and
            sponsored links. We do not control the Third-Party Services and other Materials of those websites or of such
            third parties (including, without limitation, our partners) and accept no responsibility for them or for any
            loss or damage that may arise from your use of them. If you decide to access any of the third-party websites
            linked to the Services, you do so entirely at your own risk and subject to any terms of use, privacy
            policies, or other terms and conditions of use for such third-party websites.
        </div>
        <div>
            The Services may provide certain social media features that enable you to:
            <ul className="list-disc pl-6">
                <li>
                    Link from your own or certain third-party websites to certain Materials on the Services;
                </li>
                <li>
                    Send e-mails or other communications with certain of the Materials on the Services, or links
                    thereto; or
                </li>
                <li>
                    Cause limited portions of the Materials on the Services to be displayed or appear to be displayed on
                    your own or certain third-party websites.
                </li>
            </ul>
        </div>
        <div>
            You may use these features solely as they are provided by us, including with any citations or credits to
            third-party sources, solely with respect to the specific Materials that they are displayed with, and
            otherwise in accordance with any additional terms and conditions we provide with respect to such features.
            Subject to the foregoing, you must not:
            <ul className="list-disc pl-6">
                <li>
                    Establish a link from any website that is not owned by you;
                </li>
                <li>
                    Cause the Services or any portions of them to be displayed, or appear to be displayed by, for
                    example, framing, deep linking or in-line linking, on any other website; or
                </li>
                <li>
                    Otherwise take any action with respect to the Materials that is inconsistent with any other
                    provision of these Terms of Use.
                </li>
            </ul>
        </div>
        <div>
            The website from which you are linking, or on which you make certain content accessible, must comply in all
            respects with the Content Standards set out in these Terms of Use.
        </div>
        <div>
            You agree to cooperate with us in causing any unauthorized framing or linking immediately to cease. We
            reserve the right to withdraw linking permission without notice.
            We may disable all or any social media features and any links at any time without notice in our discretion.
        </div>
    </>
);

const PointsAndRewards = () => (
    <>
        <H2 variant="type-headline-small">Points and Rewards</H2>
        <div>
            You may be able to accumulate points (“Points”) by engaging with certain Materials and Third-Party Services
            made available on or through the Services (including on websites of our third-party partners) (such
            Materials and Third-Party Services, “Experiences”). When you access an Experience, we or the Experience will
            notify you of the amount of Points that you may accumulate by engaging with such Experience as well as what
            you must do to accumulate these Points. For example, to accumulate Points by engaging with an Experience,
            you may be required to watch a video or complete a quiz made available on or through such Experience. You
            may access and view the amount of Points that you have accumulated through your account; however, Points may
            not become available to access or view on your account immediately after you complete engagement with an
            Experience. Restrictions, such as age limitations, may apply to how you can accumulate Points, and our
            practice is to notify you of any such restrictions.
        </div>
        <div>
            When we issue Points, you obtain a limited license to those Points, which do not (a) have an equivalent
            value in real currency; (b) act as a substitute for real currency; or (c) earn interest. Points are not
            redeemable or exchangeable for real or virtual currency, monetary value, or convertible for other credits or
            tokens from us or any other third party, except as expressly provided in these Terms of Use or otherwise
            required by applicable law. Your license to use Points will terminate upon termination of these Terms of Use
            or your account and as otherwise provided herein, except as otherwise required by applicable law. You may
            not transfer, assign, sell, gift, exchange, trade, convert, lease, sublicense, rent, or distribute Points
            except through the Services and as expressly permitted by us. Any disposition or attempted disposition of
            Points in violation of these Terms of Use will be void and may result in immediate termination of your
            account and your license to use Points. We, in our sole discretion, have the absolute right to manage,
            modify, suspend, revoke, and terminate your license to use Points without notice, refund, compensation, or
            liability to you, except as otherwise prohibited by applicable law.
        </div>
        <div>
            We may, from time to time, provide ways for you to use your Points, such as to redeem them for gift cards or
            other rewards (“Rewards”). Gift cards are valid at participating merchants only, and are subject to the
            issuer’s terms and conditions, which, if permitted by applicable law, may address expiration policies and
            non-usage fees and may be changed at the sole discretion of the issuer. Redemption values for Rewards may
            vary, and the amount of time it takes for you to receive a Reward may vary. Restrictions, such as minimum or
            maximum amounts, may apply to how you use your Points, and our practice is to notify you of any such
            restrictions. Users who are or become 20 years of age or older may be subject to additional limitations on
            their ability to accumulate and redeem Points; for example, such users may not be able to redeem Points for
            Rewards, even if such Points were accumulated before the user reached 20 years of age.
        </div>
        <div>
            Notwithstanding the foregoing, neither we nor any third party has any obligation to exchange Points for
            anything of value, including, but not limited to, gift cards or real currency, except as expressly provided
            in these Terms of use or otherwise required by applicable law. We make no guarantee as to the nature,
            quality, or value of Points or the availability or supply thereof.
        </div>
        <div>
            You are responsible for how your Points are used. Once your Points have been used, the transaction in which
            your Points were used is considered final and may not be canceled unless otherwise noted. We are not
            responsible for how your redeemed Rewards are used.
        </div>
        <div>
            We may, in our sole discretion, revise and/or update any features or functionalities related to Points,
            including, without limitation, how you accumulate Points, how you use Points, the amount of points
            associated with engaging with any Experience, the value of Points (including, without limitation, by
            increasing or decreasing the amount of your Points), and what Rewards may be redeemed for Points. We may
            also, in our sole discretion, for any or no reason (including for any violation by you of these Terms of
            Use), temporarily or permanently prohibit you from accumulating Points, using Points that you have already
            accumulated (including to redeem such Points for Rewards), or accessing or using any other features or
            functionalities related to Points.
        </div>
        <div>
            We do not control the Experiences made available by third parties and/or the website on which you can redeem
            your Points for Rewards, and we accept no responsibility for such Experiences or website or for any loss or
            damage that may arise from your access to and engagement with them. If you decide to access or engage with
            any Experiences made available by third parties and/or the website on which you can redeem your Points for
            Rewards, you do so entirely at your own risk and subject to the applicable third party’s terms of use,
            privacy policies, or other terms and conditions of use applicable to such Experiences or websites.
        </div>
    </>
);

const ContestsAndSweepstakes = () => (
    <>
        <H2 variant="type-headline-small">Contests and Sweepstakes</H2>
        <div>
            We may, from time to time, in our sole discretion, make available contests, sweepstakes, and other
            promotions on or through the Services, including on websites of our third-party partners
            (“Sweepstakes”). Any Sweepstakes will be subject to supplemental terms and conditions in addition to
            these Terms of Use (“Sweepstakes Terms”), which may include eligibility criteria and other participation
            requirements. If you wish to participate in any Sweepstakes, please first read the applicable
            Sweepstakes Terms carefully. If the terms and conditions of any Sweepstakes Terms conflict with these
            Terms of Use, then such conflicting terms and conditions of the Sweepstakes Terms will govern with
            respect to the applicable Sweepstakes. All content that you submit to the Services in connection with
            any Sweepstakes constitutes User Contributions and is subject to all provisions of these Terms of Use
            applicable to User Contributions.
        </div>
    </>
);

const ChangesToServices = () => (
    <>
        <H2 variant="type-headline-small">Changes to the Services</H2>
        <div>
            We may update the Materials made available on or through the Services from time to time, but the Services’
            Materials are not necessarily complete or up-to-date. Any of the Materials may be out of date at any given
            time, and we are under no obligation to update such Materials.
        </div>
    </>
);

const RelianceOnInformationPosted = () => (
    <>
        <H2 variant="type-headline-small">Reliance on Information Posted</H2>
        <div>
            The information presented on or through the Services (including, without limitation, information presented
            on or through any Materials or Third-Party Services) is made available solely for general information
            purposes. This includes any information or content generated or provided through AI-enabled features. We do
            not warrant the accuracy, completeness, or usefulness of this information. Any reliance you
            place on such information is strictly at your own risk. We disclaim all liability and responsibility arising
            from any reliance placed on such information by you or any other visitor to the Services, or by anyone who
            may be informed of such information.
        </div>
        <div>
            The Services may include Third-Party Services and other Materials from or provided by
            third parties, including third-party partners, licensors, syndicators, aggregators, and/or
            reporting services. All statements and/or opinions expressed in these Third-Party
            Services and/or Materials, other than the Materials expressly identified as being provided
            by Britebound, are solely the opinions and the responsibility of the person or entity
            providing those Third-Party Services and/or Materials. These Third-Party Services and/or
            Materials do not necessarily reflect the opinions of Britebound. We expressly disclaim all
            liability for, and we are not responsible, or liable to you or any third party, for the content
            or accuracy of any Third-Party Services and/or Materials provided by any third parties.
        </div>
    </>
);

const DisclaimerOfWarranties = () => (
    <>
        <H2 variant="type-headline-small">Disclaimer of Warranties</H2>
        <div>
            YOUR USE OF, AND YOUR RELIANCE ON OR ACTIONS OR INACTION BASED ON, THE
            SERVICES OR ANY WEBSITES LINKED TO OR FROM THEM, OR ANY OF THE THIRD-PARTY SERVICES OR MATERIALS AVAILABLE ON
            OR THROUGH, THE SERVICES OR SUCH OTHER WEBSITES, IS AT YOUR OWN RISK. THE SERVICES AND THEIR
            MATERIALS AND ANY LINKS TO THIRD-PARTY SERVICES AND THIRD-PARTY
            WEBSITES ARE PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS, WITHOUT ANY
            WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, STATUTORY OR
            OTHERWISE, INCLUDING BUT NOT LIMITED TO ANY WARRANTIES OF
            MERCHANTABILITY, NON-INFRINGEMENT, OR FITNESS FOR ANY PARTICULAR
            PURPOSE. NEITHER BRITEBOUND NOR ANY PERSON ASSOCIATED WITH
            BRITEBOUND MAKES ANY WARRANTY OR REPRESENTATION WITH RESPECT TO THE
            COMPLETENESS, SECURITY, RELIABILITY, QUALITY, ACCURACY, OR AVAILABILITY OF
            THE SERVICES, THEIR MATERIALS, OR ANY LINKS TO THIRD-PARTY SERVICES OR
            THIRD-PARTY WEBSITES. WITHOUT LIMITING THE FOREGOING, NEITHER
            BRITEBOUND NOR ANYONE ASSOCIATED WITH BRITEBOUND REPRESENTS OR
            WARRANTS THAT THE SERVICES OR ANY OF THEIR MATERIALS OR ANY LINKS TO
            THIRD-PARTY SERVICES OR THIRD-PARTY WEBSITES WILL BE ACCURATE, RELIABLE,
            ERROR-FREE, OR UNINTERRUPTED, THAT DEFECTS WILL BE CORRECTED, THAT OUR
            SERVICES, MATERIALS, OR ANY THIRD-PARTY SERVICES OR THIRD-PARTY LINKS, OR
            THE SERVERS THAT MAKE THEM AVAILABLE ARE FREE OF VIRUSES OR OTHER
            HARMFUL COMPONENTS, OR THAT THE THEY WILL OTHERWISE MEET YOUR NEEDS
            OR EXPECTATIONS. THE FOREGOING DOES NOT AFFECT ANY WARRANTIES WHICH
            CANNOT BE EXCLUDED OR LIMITED UNDER APPLICABLE LAW.
        </div>
    </>
);

const LimitationOfLiability = () => (
    <>
        <H2 variant="type-headline-small">Limitation on Liability</H2>
        <div>
            TO THE FULLEST EXTENT PROVIDED BY LAW, IN NO EVENT WILL BRITEBOUND, ITS
            AFFILIATES, OR ITS OR THEIR LICENSORS, SERVICE PROVIDERS, EMPLOYEES,
            AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR DAMAGES OF ANY KIND, UNDER
            ANY LEGAL THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF, OR
            INABILITY TO USE, OR RELIANCE ON OR ACTIONS OR INACTION BASED ON, THE
            SERVICES, OR ANY WEBSITES LINKED TO OR FROM THEM, OR ANY OF THE THIRD-PARTY SERVICES OR MATERIALS AVAILABLE ON
            OR THROUGH, THE SERVICES OR SUCH OTHER WEBSITES, INCLUDING ANY DIRECT, INDIRECT, SPECIAL, INCIDENTAL,
            CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO,
            PERSONAL INJURY, PAIN AND SUFFERING, EMOTIONAL DISTRESS, LOSS OF
            REVENUE, LOSS OF PROFITS, LOSS OF BUSINESS OR ANTICIPATED SAVINGS, LOSS
            OF USE, LOSS OF GOODWILL, LOSS OF DATA, AND WHETHER CAUSED BY TORT
            (INCLUDING NEGLIGENCE), BREACH OF CONTRACT OR OTHERWISE, EVEN IF
            FORESEEABLE.
        </div>
        <div>
            TO THE FULLEST EXTENT PROVIDED BY LAW, WE WILL NOT BE LIABLE FOR ANY LOSS OR DAMAGE CAUSED BY A DISTRIBUTED
            DENIAL-OF-SERVICE ATTACK, VIRUSES, OR OTHER TECHNOLOGICALLY HARMFUL MATERIAL THAT MAY INFECT YOUR COMPUTER
            EQUIPMENT, COMPUTER PROGRAMS, DATA, OR OTHER PROPRIETARY MATERIAL DUE TO YOUR USE OF THE SERVICES, ANY
            THIRD-PARTY WEBSITES LINKED TO OR FROM THE SERVICES, OR THIRD-PARTY SERVICES OR MATERIALS AVAILABLE THROUGH
            THE SERVICES OR SUCH OTHER WEBSITES, OR DUE TO YOUR DOWNLOADING OF ANY MATERIALS.
        </div>
        <div>
            THE FOREGOING DOES NOT AFFECT ANY LIABILITY WHICH CANNOT BE EXCLUDED OR LIMITED UNDER APPLICABLE LAW.
        </div>
    </>
);

const Indemnification = () => (
    <>
        <H2 variant="type-headline-small">Indemnification</H2>
        <div>
            You agree, to the maximum extent permissible under applicable law, to defend,
            indemnify, and hold harmless Britebound, its affiliates, licensors, and service providers,
            and its and their respective officers, directors, employees, contractors, agents, licensors,
            suppliers, successors, and assigns from and against all claims, demands, losses,
            damages, liabilities, demands, judgments, awards, settlements, costs and expenses of
            any kind (including legal fees and expenses) relating to or arising out of: (i) your access
            to, use, or misuse of the Services or any of their Materials (including relating to User
            Contributions) or Third-Party Services made available on or through the Services; (ii) your
            breach, alleged breach, or violation of these Terms of Use; (iii) your failure to comply with
            applicable law (including any failure to obtain or provide any necessary consent or
            notice); or (iv) the infringement by you or any third party using your account of any
            intellectual property, privacy, or other right of any person or entity.
        </div>
    </>
);

const GoverningLawAndJurisdiction = () => (
    <>
        <H2 variant="type-headline-small">Governing Law and Jurisdiction; Limitation on Time to File Claims</H2>
        <div>
            These Terms of Use shall be interpreted in accordance with the internal laws of the Commonwealth of
            Massachusetts without giving effect to any choice or conflict of law provision or rule, whether of the
            Commonwealth of Massachusetts or any other jurisdiction.
        </div>
        <div>
            You agree that any legal suit, action, or proceeding arising out of, or related to, these Terms of Use or
            the Services that is not resolved by binding arbitration as set forth below will be instituted exclusively
            in the federal and state courts located in Suffolk County of the Commonwealth of Massachusetts. However,
            subject to the arbitration provisions set forth below, we retain the right to bring any suit, action, or
            proceeding against you for breach of these Terms of Use in your state of residence or any other relevant
            state. You waive any and all objections to the exercise of jurisdiction over you by such courts and to venue
            in such courts.
        </div>
    </>
);

const Arbitration = () => (
    <>
        <H2 variant="type-headline-small">Arbitration</H2>
        <div>
            PLEASE READ THIS SECTION CAREFULLY BECAUSE IT AFFECTS YOUR RIGHTS. BY AGREEING TO BINDING ARBITRATION, YOU
            WAIVE YOUR RIGHT TO LITIGATE DISPUTES THROUGH A COURT AND TO HAVE A JUDGE OR JURY DECIDE YOUR CASE.
        </div>
        <div>
            In order to expedite and control the cost of disputes, Britebound and you agree that any
            legal or equitable claim, dispute, action or proceeding arising from or related to your use
            of the Services or these Terms of Use (“Dispute”) will be resolved as follows to the fullest
            extent permitted by law:
            <ul className="list-disc pl-6">
                <li>
                    Notice of Dispute. In the event of a Dispute, you or Britebound must give the other
                    a written statement that sets forth the name, address, and contact information of
                    the party giving it, the facts giving rise to the Dispute, and a proposed solution (a
                    “Notice of Dispute”). You must send any Notice of Dispute by first class U.S. Mail
                    to Britebound at 33 Arch Street Suite 2100, Boston, MA 02110, and also via e-mail
                    to
                    {' '}
                    <a className="text-blue-600 dark:text-blue-400 underline" href="mailto:support@gozig.com">support@gozig.com</a>
                    .
                    Britebound will send any Notice of Dispute to you by first class U.S. Mail to
                    your address if Britebound has it, or otherwise to your e-mail address. You and
                    Britebound will attempt to resolve any Dispute through informal negotiation within
                    sixty (60) days from the date the Notice of Dispute is sent. After sixty (60) days,
                    you and Britebound shall endeavor to settle any Dispute by mediation. The place
                    of mediation shall be Boston, Massachusetts. Any Dispute which has not been
                    resolved by mediation as provided herein within thirty (30) days after appointment
                    of a mediator or such time period as you or Britebound may otherwise agree, shall
                    be finally resolved by binding arbitration as described below. An arbitrator will
                    decide any disputes over whether this subsection has been violated, and has the
                    power to enjoin the filing or prosecution of arbitrations. Unless prohibited by
                    applicable law, the arbitrator shall not administer any arbitration unless the
                    requirements of this subsection have been met.
                </li>
                <li>
                    Binding Arbitration. You are giving up the right to litigate (or participate in as a party or class
                    member) all Disputes in court before a judge or jury. Instead, all Disputes will be resolved before
                    a neutral arbitrator, whose decision will be final except for a limited right of appeal under the
                    Federal Arbitration Act. The arbitrator shall decide all issues pertaining to arbitrability,
                    including his or her own jurisdictional validity and enforceability of these Terms of Use (e.g.,
                    unconscionability). For the avoidance of doubt, this is not meant to reduce any powers granted to
                    the arbitrator under the applicable arbitration rules. The place of arbitration shall be Boston,
                    Massachusetts. Any court with jurisdiction over the parties may enforce the arbitrator’s award.
                </li>
                <li>
                    Class Action Waiver. Any proceedings to resolve or litigate any Dispute in any
                    forum will be conducted solely on an individual basis. Neither you nor Britebound
                    will seek to have any Dispute heard as a class action or in any other proceeding in
                    which either party acts or proposes to act in a representative capacity. No
                    arbitration or proceeding will be combined with another without the prior written
                    consent of all parties to all affected arbitrations or proceedings. Class actions and
                    class arbitrations are not permitted; for example, you may bring a claim only on
                    your own behalf and cannot seek relief that would affect other guests or users.
                    Nor may an arbitrator consolidate arbitrations unless all parties agree. If there is a
                    final judicial determination that the limitations of this paragraph are unenforceable
                    as to a particular claim or a particular request for relief (such as a request for
                    injunctive relief), then the parties agree that such a claim or request for relief shall
                    be decided by a court after all other claims and requests for relief are arbitrated.
                    <ul className="list-disc pl-6">
                        <li>
                            Mass Arbitrations. If 25 or more claimants submit similar Notices of Dispute or file similar
                            arbitrations and are represented by the same or coordinated counsel, all of the cases must
                            be resolved in arbitration in stages using staged bellwether proceedings. You agree to do
                            this even though the resolution of some claims might be delayed. In the first stage, the
                            parties shall select up to 10 cases to be filed in arbitration and resolved by separate
                            arbitrators. In the meantime, no other cases may be filed in arbitration. Nor may the
                            arbitration provider accept, administer or demand payment for fees for other arbitrations.
                            If the remaining cases are not settled after the first stage is done, the parties will
                            repeat the process. These staged bellwether proceedings will continue until all cases are
                            resolved. If this paragraph applies to a Notice of Dispute, any statute of limitations
                            applicable to the listed claims will be tolled from the time the first cases are selected
                            for bellwether proceedings until the claimant’s Notice of Dispute is selected for a
                            bellwether proceeding or otherwise resolved. A court will have the authority to enforce this
                            subsection, including the power to enjoin the filing or prosecution of arbitrations or
                            assessment of related fees.
                        </li>
                        <li>
                            Arbitration Procedures. Any arbitration will be conducted by the American
                            Arbitration Association (“AAA”) under their Consumer Arbitration Rules
                            (“AAA Rules”) in effect at the time the Dispute is filed. You may request a
                            telephonic or in-person hearing by following the AAA Rules. In a dispute
                            involving $10,000 or less, any hearing will be telephonic unless the
                            arbitrator finds good cause to hold an in-person hearing instead. To the
                            extent the forum provided by AAA is unavailable, Britebound and you
                            agree to select a mutually agreeable alternative dispute resolution service
                            and that such alternative dispute resolution service shall apply the AAA
                            Rules. The arbitrator may award the same damages to you individually as a
                            court could. The arbitrator may award declaratory or injunctive relief only to
                            you individually, and only to the extent required to satisfy your individual
                            claim.
                        </li>
                        <li>
                            Arbitration Fees. Whoever files the arbitration pays the initial filing fee. If
                            Britebound files, then Britebound will pay; if you file, then you will pay
                            unless you get a fee waiver under the applicable arbitration rules. Each
                            party will bear the expense of that party’s attorneys, experts, and
                            witnesses, and other expenses, regardless of which party prevails, but a
                            party may recover any or all expenses (including attorney’s fees) from
                            another party if the arbitrator, applying applicable law, so determines.
                        </li>
                        <li>
                            Filing Period. To the extent permitted by law, any Dispute under these
                            Terms of Use must be filed within one (1) year in an arbitration proceeding.
                            The one-year period begins on the earliest date when any of the alleged
                            claims first accrue, regardless of whether additional damages occur after
                            such claims first accrue. If a Dispute is not filed within one year, it is
                            permanently barred. This period can only be extended by the written
                            consent of both you and Britebound. No statutes or provisions of law that
                            would toll or otherwise affect the time in which you may bring a claim shall
                            operate to extend the period limited in this section, and you hereby waive
                            any such statutes and provisions, to the fullest extent permitted by law.
                        </li>
                        <li>
                            Enforceability. If the waiver of class actions above is found unenforceable,
                            or this entire section is found unenforceable, then this entire section will be
                            null and void. If that happens, you and Britebound agree that the section
                            below on governing law and jurisdiction will govern any Dispute.
                        </li>
                        <li>
                            Opt-Out. You can opt out of arbitration within 30 days of the date that you first agreed to
                            these Terms of Use (including any earlier version containing an arbitration provision). If
                            you have previously agreed to arbitration, then you may opt out of any future revisions to
                            the arbitration provision within 30 days of receiving notice of the updated arbitration
                            provision, in which case the prior version of the arbitration shall apply. To opt out of
                            arbitration (or revisions to this arbitration provision), you must send your name, residence
                            address, username, email address or phone number you use for your Services account, and a
                            clear statement that you want to opt out of this arbitration agreement (or of the revisions
                            to it), and you must send them here: 33 Arch Street Suite 2100, Boston, MA 02110.
                        </li>
                    </ul>
                </li>
            </ul>
        </div>
        <div>
            BRITEBOUND AFFILIATES, AGENTS, EMPLOYEES, AND SUBCONTRACTORS ARE
            INTENDED AS THIRD-PARTY BENEFICIARIES OF THE ARBITRATION CLAUSES IN THIS SECTION.
        </div>
    </>
);

const Waiver = () => (
    <>
        <H2 variant="type-headline-small">Waiver, Interpretation and Severability</H2>
        <div>
            No waiver by Britebound of any term or condition set forth in these Terms of Use shall be
            deemed a further or continuing waiver of such term or condition, or a waiver of any other
            term or condition, and any failure of Britebound to assert a right or provision under these
            Terms of Use shall not constitute a waiver of such right or provision.
        </div>
        <div>
            For purposes of interpreting these Terms of Use, all references to “including” shall be understood to mean
            “including but not limited to”.
        </div>
        <div>
            If any provision of these Terms of Use is held by a court or other tribunal of competent jurisdiction to be
            invalid, illegal, or unenforceable for any reason, such provision shall be eliminated or limited to the
            minimum extent such that the remaining provisions of these Terms of Use will continue in full force and
            effect.
        </div>
    </>
);

const EntireAgreement = () => (
    <>
        <H2 variant="type-headline-small">Entire Agreement</H2>
        <div>
            These Terms of Use constitute the sole and entire agreement between you and
            Britebound with respect to the Services and supersede all prior and contemporaneous
            understandings, agreements, representations and warranties, both written and oral, with
            respect to the Services.
        </div>
    </>
);

const CopyrightInfringement = () => (
    <>
        <H2 variant="type-headline-small">Copyright Infringement</H2>
        <div>
            Britebound takes copyright infringement seriously and complies with the Digital
            Millennium Copyright Act (DMCA). If you believe that any of the Materials or User
            Contributions available through, or displayed on, the Services violates your copyright,
            please notify us at
            {' '}
            <a className="text-blue-600 dark:text-blue-400 underline" href="mailto:support@gozig.com">support@gozig.com</a>
            , and submit a notice to our authorized copyright agent, who will accept and review all claims submitted to
            it. In order for us to take action upon receiving such notice, the notice must contain the following:
            <ul className="list-disc pl-6">
                <li>
                    A physical or electronic signature of a person authorized to act on behalf of the owner of an
                    exclusive right that is allegedly infringed;
                </li>
                <li>
                    Identification of the copyrighted work claimed to have been infringed, or, if multiple copyrighted
                    works at a single online site are covered by a single notification, a representative list of such
                    works at that site;
                </li>
                <li>
                    Identification of the material that is claimed to be infringing or to be the subject of infringing
                    activity and that is to be removed or access to which is to be disabled, and information reasonably
                    sufficient to permit the service provider to locate the material;
                </li>
                <li>
                    Information reasonably sufficient to permit the service provider to contact the complaining party,
                    such as an address, telephone number, and, if available, an electronic mail address at which the
                    complaining party may be contacted;
                </li>
                <li>
                    A statement that the complaining party has a good faith belief that use of the material in the
                    manner complained of is not authorized by the copyright owner, its agent, or the law; or
                </li>
                <li>
                    A statement that the information in the notification is accurate, and under penalty of perjury, that
                    the complaining party is authorized.
                </li>
            </ul>
        </div>
        <div>
            Please submit your notice to: Britebound Legal Department 33 Arch Street Suite 2100 Boston, MA 02110
        </div>
        <div>
            Upon receiving notice in accordance with the policy that such Materials are infringing, we will expeditiously take down such Materials.
        </div>
        <div>
            It is our policy to terminate the user accounts of repeat or intentional infringers.
        </div>
    </>
);

const YourCommentsAndConcerns = () => (
    <>
        <H2 variant="type-headline-small">Your Comments and Concerns</H2>
        <div>
            The Services are operated by Massachusetts Higher Education Assistance Corporation, d/b/a Britebound, 33 Arch Street, Suite 2100, Boston, Massachusetts 02110.
        </div>
        <div>
            All feedback, comments, requests for technical support, and other communications relating to the Services should be directed to:
            {' '}
            <a className="text-blue-600 dark:text-blue-400 underline" href="mailto:support@gozig.com">support@gozig.com</a>
        </div>
    </>
);

export const TermsAndConditions = ({ showTitle, showLastModified }: TermsAndConditionsProps) => (
    <DesktopContainer>
        <div className="flex flex-col gap-10 px-6">
            {(showTitle || showLastModified) && (
                <div className="flex flex-col gap-4">
                    {showTitle && (
                        <H1 variant="type-headline-large">Terms & Conditions</H1>
                    )}
                    {showLastModified && (
                        <div>
                            Last Modified: March 23, 2026
                        </div>
                    )}
                </div>
            )}
            <div className="flex flex-col gap-4 text-base">
                <AcceptanceOfTerms />
                <AiEnabledFeatures />
                <TermsEffectiveFromAccess />
                <ChangesToTerms />
                <NoUseOutsideOfUS />
                <AccessingTheService />
                <Trademarks />
                <IntellectualPropertyRights />
                <ProhibitedUses />
                <UserContributions />
                <ContentStandards />
                <MonitoringAndEnforcement />
                <LinksToAndFromServices />
                <PointsAndRewards />
                <ContestsAndSweepstakes />
                <ChangesToServices />
                <RelianceOnInformationPosted />
                <DisclaimerOfWarranties />
                <LimitationOfLiability />
                <Indemnification />
                <GoverningLawAndJurisdiction />
                <Arbitration />
                <Waiver />
                <EntireAgreement />
                <CopyrightInfringement />
                <YourCommentsAndConcerns />
            </div>
        </div>
    </DesktopContainer>
);
